import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, Users, ShoppingBag, TrendingUp, Wallet, MousePointer, Eye, CreditCard, BarChart3, Calendar } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiFetchJson } from '../lib/api';
import { formatPrice, maskPhone } from '../lib/utils';
import { trackPageView } from '../lib/analytics';
import type { ReferralDashboardData } from '../types';
import { SITE_NAME } from '../lib/brand';

const STATUS_LABELS: Record<string, string> = {
  pending: '待確認',
  approved: '已確認',
  paid: '已發放',
  cancelled: '已取消',
};

const DAY_OPTIONS = [
  { value: 7, label: '最近 7 天' },
  { value: 30, label: '最近 30 天' },
  { value: 90, label: '最近 90 天' },
];

export default function ReferralDashboard() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<ReferralDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);

  useEffect(() => {
    trackPageView('/ref/d/:token');
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetchJson<ReferralDashboardData>(`/public/referral/dashboard/${token}?days=${days}`)
      .then((res) => {
        if (res.success && res.data) {
          setData(res.data);
        } else {
          setError(res.error || '找不到夥伴資料');
        }
        setLoading(false);
      })
      .catch(() => {
        setError('載入失敗');
        setLoading(false);
      });
  }, [token, days]);

  const handleCopy = async () => {
    if (!data?.referralLink) return;
    await navigator.clipboard.writeText(data.referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="text-center py-20">載入中...</div>
        <Footer />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{error || '無效的連結'}</h1>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{SITE_NAME} 夥伴 Dashboard</h1>
          <p className="text-white/80">歡迎加入推薦夥伴計劃</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 space-y-8">
          {/* Greeting */}
          <div className="bg-gradient-to-r from-x1493-primary to-x1493-secondary rounded-2xl p-6 text-white">
            <h2 className="text-2xl font-bold mb-1">{data.name}，你好！</h2>
            <p className="text-white/90">謝謝你將火熱分享給更多人。</p>
          </div>

          {/* Referral Link */}
          <div className="bg-stone-50 rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-3">你的專屬連結</h3>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={data.referralLink}
                className="flex-1 p-3 border border-stone-200 rounded-xl bg-white text-stone-600 text-sm"
              />
              <button
                onClick={handleCopy}
                className="bg-x1493-dark text-white px-4 py-2 rounded-xl font-bold hover:bg-black transition flex items-center gap-2"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? '已複製' : '複製'}
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <Users className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.totalBoundCustomers}</p>
              <p className="text-xs text-stone-500">綁定顧客</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <ShoppingBag className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.totalOrders}</p>
              <p className="text-xs text-stone-500">成交訂單</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <TrendingUp className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.approvedCommission}</p>
              <p className="text-xs text-stone-500">已確認回贈（分）</p>
            </div>
            <div className="bg-stone-50 rounded-2xl p-5 text-center">
              <Wallet className="mx-auto text-x1493-secondary mb-2" size={28} />
              <p className="text-2xl font-bold">{data.paidCommission}</p>
              <p className="text-xs text-stone-500">已發放回贈（分）</p>
            </div>
          </div>

          {/* Traffic Analytics */}
          <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <BarChart3 className="text-x1493-secondary" size={24} />
                流量與轉化
              </h3>
              <div className="flex items-center gap-2">
                <Calendar size={16} className="text-stone-500" />
                <select
                  value={days}
                  onChange={(e) => setDays(Number(e.target.value))}
                  className="px-3 py-1.5 border border-stone-200 rounded-lg text-sm bg-white text-stone-700 focus:ring-2 focus:ring-x1493-primary outline-none"
                >
                  {DAY_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <MousePointer className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.clicks ?? 0}</p>
                <p className="text-xs text-stone-500">連結獨立點擊</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <Eye className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.uniqueVisitors ?? 0}</p>
                <p className="text-xs text-stone-500">獨立訪客</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <ShoppingBag className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{data.traffic?.orders ?? 0}</p>
                <p className="text-xs text-stone-500">成交訂單</p>
              </div>
              <div className="bg-stone-50 rounded-xl p-4 text-center">
                <CreditCard className="mx-auto text-x1493-secondary mb-1" size={22} />
                <p className="text-xl font-bold">{formatPrice(data.traffic?.revenue ?? 0)}</p>
                <p className="text-xs text-stone-500">帶來營收</p>
              </div>
            </div>

            {/* Conversion Funnel */}
            <div className="mb-6">
              <h4 className="text-sm font-bold text-stone-700 mb-3">轉化漏斗</h4>
              <div className="space-y-2">
                {[
                  { label: '獨立點擊', value: data.traffic?.clicks ?? 0, color: 'bg-blue-500' },
                  { label: '瀏覽頁面', value: data.traffic?.pageViews ?? 0, color: 'bg-indigo-500' },
                  { label: '加入購物車', value: data.traffic?.addToCarts ?? 0, color: 'bg-purple-500' },
                  { label: '開始結帳', value: data.traffic?.beginCheckouts ?? 0, color: 'bg-pink-500' },
                  { label: '完成訂單', value: data.traffic?.orders ?? 0, color: 'bg-green-500' },
                ].map((step, idx, arr) => {
                  const max = Math.max(1, arr[0].value);
                  const prev = idx > 0 ? arr[idx - 1].value : step.value;
                  const rate = prev ? ((step.value / prev) * 100).toFixed(1) + '%' : '0%';
                  return (
                    <div key={step.label} className="flex items-center gap-3">
                      <div className="w-24 text-xs text-stone-500 text-right">{step.label}</div>
                      <div className="flex-1 h-8 bg-stone-100 rounded-lg overflow-hidden relative">
                        <div
                          className={`h-full ${step.color} transition-all duration-500`}
                          style={{ width: `${Math.min(100, (step.value / max) * 100)}%` }}
                        />
                        <span className="absolute inset-0 flex items-center px-2 text-xs font-medium text-stone-700">
                          {step.value.toLocaleString('zh-HK')} {idx > 0 && <span className="ml-2 text-stone-400">({rate})</span>}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between text-sm text-stone-600">
                <span>整體點擊轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.clickToOrderRate ?? '0%'}</span></span>
                <span>訪客轉化率：<span className="font-bold text-x1493-primary">{data.traffic?.visitorToOrderRate ?? '0%'}</span></span>
              </div>
            </div>

            {/* Top Pages */}
            {data.topPages && data.topPages.length > 0 && (
              <div>
                <h4 className="text-sm font-bold text-stone-700 mb-3">熱門頁面</h4>
                <div className="space-y-2">
                  {data.topPages.map((page, idx) => {
                    let displayPage = page.page || '/';
                    try { displayPage = decodeURIComponent(displayPage); } catch { /* keep original */ }
                    return (
                      <div key={page.page + idx} className="flex items-center justify-between text-sm">
                        <span className="text-stone-600 truncate max-w-[70%]">{displayPage}</span>
                        <span className="font-medium text-stone-800">{page.views} 次瀏覽</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Withdrawal Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 text-center">
            <p className="text-amber-800 font-medium">100分等於 HK$1，需要兌換請 WhatsApp 聯絡客服 🌶️</p>
          </div>

          {/* Recent Orders */}
          <div>
            <h3 className="text-xl font-bold mb-4">最近成交紀錄</h3>
            {data.recentOrders.length === 0 ? (
              <p className="text-stone-500">暫無成交紀錄</p>
            ) : (
              <div className="space-y-3">
                {data.recentOrders.map((order) => (
                  <div key={order.orderNum} className="bg-stone-50 rounded-xl p-4 flex flex-col sm:flex-row justify-between gap-2">
                    <div>
                      <p className="font-bold">#{order.orderNum}</p>
                      <p className="text-sm text-stone-500">{maskPhone(order.customerPhone)}</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="font-bold text-x1493-primary">{formatPrice(order.orderAmount)}</p>
                      <p className="text-sm text-stone-600">回贈 {order.commissionPoints} 分</p>
                      <span className="inline-block mt-1 bg-white px-2 py-0.5 rounded text-xs font-bold text-stone-600">
                        {STATUS_LABELS[order.status] || order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
